import api from '@/api'
import format from '@/utils/format'
import status from '@/utils/status'
import tableHeaders from '@/utils/tableHeaders'
import { defaultRecentDaysRange, toDateStr } from '@/utils/dashboard'

/**
 * 이슈보드(발행·입금) 기본 조회기간 — 관리자 대시보드와 같은 «최근 30일».
 * 목록의 발행일자/입금일자(정산 행 settlement_date, 없으면 등록일) 기준으로 서버가 거른다 (2026-09-10).
 */
function defaultIssuePeriod() {
	const [from, to] = defaultRecentDaysRange(30)
	return { startDate: toDateStr(from), endDate: toDateStr(to) }
}

/**
 * 발행·입금 목록/엑셀 요청 파라미터.
 * 검색어가 있으면 조회기간(startDate/endDate)을 **보내지 않아** 전체 기간에서 찾는다 —
 * 종전에는 검색어와 «최근 30일» 이 서버에서 AND 로 묶여 기간 밖의 접수번호·고객사가
 * 검색되지 않았다 (2026-09-10 현업 결정 (가)). 스토어의 기간 값은 그대로 두어
 * 검색어를 지우면 다시 선택된 기간으로 돌아간다.
 */
/**
 * 조회기간 칸에 보여 줄 «실제 적용 중인 기간» 문구 (2026-09-10 현업 결정 ①).
 * 기간이 걸려 있는지 화면에 표시가 없어 목록이 왜 짧은지 알 수 없었고, 검색어를 넣으면
 * 기간이 풀리는 규칙도 보이지 않았다.
 *   검색어 있음            → «전체 기간 (검색어 적용 중)»
 *   선택 없음(기본 30일)   → «최근 30일 (YYYY-MM-DD ~ YYYY-MM-DD)»
 *   연도/월/분기/반기 선택 → «YYYY-MM-DD ~ YYYY-MM-DD»
 */
export function issueAppliedPeriodText(issueParams, periodSel) {
	if (String(issueParams?.keyword ?? '').trim() !== '') return '전체 기간 (검색어 적용 중)'
	const range = issueParams?.startDate && issueParams?.endDate
		? `${issueParams.startDate} ~ ${issueParams.endDate}`
		: ''
	if (!range) return '전체 기간'
	return periodSel ? range : `최근 30일 (${range})`
}

export function toIssueApiParams(issueParams) {
	const status = issueParams.filter?.status || ''
	const apiParams = {
		...issueParams,
		category: (status === 'PUBLISH' || status === 'PAID') ? status : (issueParams.filter?.category || '')
	}
	if (String(issueParams.keyword ?? '').trim() !== '') {
		delete apiParams.startDate
		delete apiParams.endDate
	}
	return apiParams
}

const state = {
	/**
	 * 현재 선택된 탭 (WAIT | PUBLISH | PAID | COMPLETE).
	 *
	 * 화면 컴포넌트의 data() 에 두면 라우터 이동으로 컴포넌트가 파괴될 때 값이 사라지는데,
	 * 목록 조건(settlementParams.filter.status)은 이 스토어에 남는다. 그래서 목록에서
	 * 접수번호 → 업무 상세 → 뒤로가기 를 하면 **탭은 정산예정, 목록은 정산완료** 처럼
	 * 서로 어긋났다. 탭과 목록 조건을 같은 곳에 두어 항상 함께 복원되게 한다.
	 */
	selectedTab: 'WAIT',
	// 정산 리스트
	settlementHeaders: [],
	settlementList: [],
	settlementCount: 0,
	settlementParams: {
		keyword: '',
		page: 1,
		count: 30,
		filter: { status: 'WAIT' }
	},
	// 이슈보드 리스트
	issueList: [],
	issueCount: 0,
	issueParams: {
		page: 1,
		count: 30,
		keyword: '',
		// 발행일자/입금일자 기준 조회기간 — 관리자 대시보드와 같은 «최근 30일» 기본값
		// (2026-08-21 현업 확인). 사용자가 기간을 바꾸면 그 값으로 대체된다.
		...defaultIssuePeriod(),
		filter: { status: 'PUBLISH' }
	},
	/**
	 * 조회기간 선택기 상태 (null = 기본 최근 30일). 화면 data() 에 두면 업무 상세 → 뒤로가기 때
	 * 선택기는 비는데 issueParams 의 기간은 남아 화면 표시와 실제 조건이 어긋났다 (2026-09-10).
	 * 새로고침은 스토어가 비므로 기간·선택기 모두 초기화된다 (현업 결정: 초기화 유지).
	 */
	issuePeriodSel: null,
	// 이슈 등록용 프로젝트 목록
	issueProjectList: [],
	// 이슈보드 필터 옵션
	issueFilterList: {
		typeCode: {
			title: '업무 코드',
			options: []
		},
		code: {
			title: '접수번호',
			options: []
		}
	},
	// 필터 옵션
	filterList: {
		category: {
			title: '업무 코드',
			options: []
		},
		code: {
			title: '접수번호',
			options: []
		},
		accountClient: {
			title: '회계고객사',
			options: []
		}
	}
}

const getters = {
	// 현재 선택된 탭 (컴포넌트가 파괴돼도 유지되어 목록 조건과 항상 함께 복원된다)
	selectedTab: (state) => state.selectedTab,
	/**
	 * 정산 헤더 — 공통 표는 field.isSort 로 클릭 가능 여부·화살표를, field.sort('ASC'|'DESC')로
	 * 활성 화살표와 다음 토글 방향을 정한다. 현재 정렬 조건(settlementParams.sort)을 헤더에 실어
	 * 어느 열로 정렬 중인지 보이게 한다 (2026-09-21 전 열 정렬 복구).
	 */
	settlementHeaders: (state) => {
		const { sort, sortDirection } = state.settlementParams || {}
		return tableHeaders.settlementListTableHeader.map(header => {
			return {
				...header,
				sort: header.isSort && sort === header.key ? (sortDirection || null) : null,
				formatter: (value, key, item) => {
					if(key.indexOf('Date') >= 0) {
						value = format.date({date: value, onlyDate: true})
					}
					else if(key === 'totalAmount' || key === 'amount' || key === 'unpaidAmount' || key === 'publishedAmount' || key === 'unpublishedAmount') {
						value = `${format.number(value)}원`
					}
					return value;
				}
			}
		})
	},
	// 정산 리스트 (등록일 기준 isNew: 최근 7일 이내)
	settlementList: (state) => {
		const now = Date.now()
		const newDays = 7
		const newMs = newDays * 24 * 60 * 60 * 1000
		return state.settlementList.map((item) => {
			// 미수금액 계산: 계약금액 - 입금금액
			item.unpaidAmount = (item.totalAmount || 0) - (item.amount || 0)
			item.unpublishedAmount = (item.totalAmount || 0) - (item.publishedAmount || 0)
			// 등록일(createDatetime) 기준 신규 여부
			const regDate = item.createDatetime ? new Date(item.createDatetime).getTime() : 0
			item.isNew = regDate > 0 && (now - regDate) <= newMs
			
			return item
		})
	},
	settlementCount: (state) => state.settlementCount,
	settlementParams: (state) => state.settlementParams,
	/**
	 * 이슈보드 헤더 — 선택된 탭에 따라 꼬리 컬럼(담당자·일시 라벨)이 달라진다.
	 *   발행탭: 요청자 / 발행자 / 발행일자
	 *   입금탭: 입력자 / 확인자 / 입금일자
	 * 데이터 키는 같고 라벨만 바뀐다 (2026-08-20 현업 2-1-1·2-2-1).
	 * 날짜 컬럼은 서버의 settlementDate(정산 행 발행/입금일자)를 날짜만 표시한다 (2026-09-10 현업 2-1·2-2).
	 */
	issueHeaders: (state) => {
		const tail = state.selectedTab === 'PAID'
			? tableHeaders.settlementIssuePaidTail
			: tableHeaders.settlementIssuePublishTail
		const { sort, sortDirection } = state.issueParams || {}
		return [...tableHeaders.settlementIssueBaseHeader, ...tail].map((header) => {
			return {
				...header,
				// 현재 정렬 열에만 활성 화살표 (2026-09-21 전 열 정렬)
				sort: header.isSort && sort === header.key ? (sortDirection || null) : null,
				formatter: (value, key, item) => {
					if (key.indexOf('Date') >= 0) {
						// settlementDate(발행/입금일자)는 상세 모달과 같이 날짜만. 등록 시각(createDatetime)은
						// 더 이상 컬럼이 아니다 (2026-09-10 현업 2-1·2-2: 상세의 발행일자와 다르게 보였음)
						value = format.date({ date: value, onlyDate: true })
					} else if (key === 'status') {
						value = status.issue.filter((el) => el.value === value)[0]
					} else if (key === 'amount') {
						// 정산 배치를 못 찾은 레거시 이슈는 금액이 null 로 온다 — 0원으로 속이지 않는다
						value = value == null ? '-' : `${format.number(value)}원`
					}
					return value
				}
			}
		})
	},
	// 이슈보드 리스트 (등록일 기준 isNew: 최근 1일 이내)
	issueList: (state) => {
		const now = Date.now()
		const newDays = 1
		const newMs = newDays * 24 * 60 * 60 * 1000
		return state.issueList.map(item => {
			item.status = {
				value: item.status,
				...status.issue.filter(el => el.value === item.status)[0]
			}
			// category 한글 변환은 «분류» 열과 함께 걷어냈다. 탭이 곧 분류라서
			// 목록에는 표시하지 않는다 (원본 코드값 PUBLISH/PAID 를 그대로 둔다).
			const regDate = item.createDatetime ? new Date(item.createDatetime).getTime() : 0
			item.isNew = regDate > 0 && (now - regDate) <= newMs
			return item;
		})
	},
	issueCount: (state) => state.issueCount,
	issueParams: (state) => state.issueParams,
	issuePeriodSel: (state) => state.issuePeriodSel,
	issueAppliedPeriodText: (state) => issueAppliedPeriodText(state.issueParams, state.issuePeriodSel),
	issueProjectList: (state) => state.issueProjectList,
	issueFilterList: (state) => state.issueFilterList,
	filterList: (state) => state.filterList
}

const mutations = {
	// 탭 선택 상태 (목록 조건과 한 곳에서 관리)
	setSelectedTab: (state, tab) => {
		state.selectedTab = tab
	},
	// 정산 관련
	setSettlementParams: (state, data) => {
		state.settlementParams = { ...state.settlementParams, ...data }
	},
	resetSettlementParams: (state) => {
		state.settlementParams = {
			keyword: '',
			page: 1,
			count: 30,
			filter: { status: 'WAIT' }
		}
	},
	setSettlementList: (state, data) => {
		state.settlementList = [...data.list]
		state.settlementCount = data.count
	},
	// 이슈보드 관련
	setIssueParams: (state, data) => {
		state.issueParams = { ...state.issueParams, ...data }
	},
	setIssuePeriodSel: (state, sel) => {
		state.issuePeriodSel = sel || null
	},
	resetIssueParams: (state) => {
		// 탭을 바꿔도 조회기간 기본값(최근 30일)은 다시 세운다 (선택기도 함께 해제)
		state.issuePeriodSel = null
		state.issueParams = {
			page: 1,
			count: 30,
			keyword: '',
			...defaultIssuePeriod(),
			filter: { status: 'PUBLISH' }
		}
	},
	setIssueList: (state, data) => {
		state.issueList = [...data.list]
		state.issueCount = data.count
	},
	setIssueProjectList: (state, data) => {
		state.issueProjectList = data
	},
	// 이슈보드 필터 옵션 설정
	setIssueFilterList: (state, data) => {
		if (data.code) {
			state.issueFilterList.code.options = data.code.map(item => ({
				text: item.text,
				value: item.value
			}))
		}
		if (data.typeCode) {
			state.issueFilterList.typeCode.options = data.typeCode.map(item => ({
				text: item.text,
				value: item.value
			}))
		}
	},
	// 필터 옵션 설정
	setFilterList: (state, data) => {
		if (data.category) {
			state.filterList.category.options = data.category.map(item => ({
				text: item.text,
				value: item.value
			}))
		}
		if (data.code) {
			state.filterList.code.options = data.code.map(item => ({
				text: item.text,
				value: item.value
			}))
		}
		if (data.accountClient) {
			state.filterList.accountClient.options = data.accountClient.map(client => ({
				text: client.text,
				value: client.value
			}))
		}
	}
}

const actions = {
	// 정산 필터 리스트 조회 (초기 로드용)
	loadFilterList: async ({ commit }, params = { count: 30 }) => {
		const res = await api.project.getSettleFilterList(params)
		commit('setFilterList', { category: res.category, code: res.code, accountClient: res.accountClient })
		return res
	},
	// 정산 필터 옵션 검색 (실시간 검색용)
	searchFilterOptions: async ({ commit }, { type, keyword, count = 30 }) => {
		const res = await api.project.getSettleFilterList({ type, keyword, count })
		if (type === 'category') {
			commit('setFilterList', { category: res.category })
		} else if (type === 'code') {
			commit('setFilterList', { code: res.code })
		} else if (type === 'accountClient') {
			commit('setFilterList', { accountClient: res.accountClient })
		}
		return res
	},
	// 정산 리스트 조회 (회계고객사별)
	loadSettlementList: async ({ commit, state }, params) => {
		const res = await api.project.getSettleList(params ? params : state.settlementParams)
		commit('setSettlementList', res)
		return res
	},
	downloadSettlementExcel: async ({ state }, params) => {
		const res = await api.project.downloadSettleListExcel(params || state.settlementParams)
		return res
	},
	/** 발행·입금 탭 엑셀 — 화면 조건 그대로, 페이지 파라미터는 빼고 전체 건을 받는다 */
	downloadIssueExcel: async ({ state }, params) => {
		const p = toIssueApiParams(params || state.issueParams)
		delete p.page
		delete p.count
		return api.project.downloadSettleIssueExcel(p)
	},
	// 이슈보드 필터 목록 조회 (접수번호)
	loadIssueFilterList: async ({ commit }, params = {}) => {
		const res = await api.project.getSettleIssueFilterList(params)
		commit('setIssueFilterList', { code: res.code, typeCode: res.typeCode })
		return res
	},
	// 이슈보드 조회 (project_account_issue 테이블)
	loadIssueList: async ({ commit, state }, params) => {
		const issueParams = params ? params : state.issueParams
		// filter.status가 PUBLISH/PAID인 경우 category로 매핑, 검색어가 있으면 기간 제외
		const res = await api.project.getSettleIssueList(toIssueApiParams(issueParams))
		commit('setIssueList', res)
		return res
	},
	// 이슈보드 상태 변경 (project_account_issue 테이블)
	updateIssueStatus: async ({ dispatch, state }, { id, status }) => {
		const res = await api.project.updateSettleIssueStatus(id, status)
		dispatch('loadIssueList', state.issueParams)
		return res
	},
	// 정산 등록 (여러 프로젝트에 대해 반복 호출)
	addSettlement: async ({ dispatch }, { projectId, body }) => {
		const res = await api.project.addSettlement(projectId, body)
		return res
	},
	// 이슈보드 등록 (project_account_issue 테이블, category 포함)
	addIssue: async ({ dispatch, state }, body) => {
		// body: { projectId, content, category }
		const res = await api.project.addSettleIssue(body)
		dispatch('loadIssueList', state.issueParams)
		return res
	},
	// 정산 등록 삭제 (마스터 + 연관 정산/이슈 삭제)
	deleteSettlementRegistration: async ({ dispatch, state }, id) => {
		const res = await api.project.deleteSettlementRegistration(id)
		await dispatch('loadSettlementList', state.settlementParams)
		await dispatch('loadIssueList', state.issueParams)
		return res
	},
	// 이슈보드 프로젝트 목록 조회 (등록 시 검색용)
	loadIssueProjectList: async ({ commit }, params) => {
		const res = await api.project.getSettleIssueProjectList(params)
		commit('setIssueProjectList', res.list || [])
		return res
	}
}

export default { 
	namespaced: true, 
	state, 
	getters, 
	mutations, 
	actions 
}
